import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import moment from 'moment';
import ReactMarkdown from 'react-markdown';
import gfm from 'remark-gfm';

import LifecycleEventChips from './LifecycleEventChips';
import MarkdownSafeBoundary from './MarkdownSafeBoundary';
import { safeMarkdownLink } from './safeMarkdownLink';
import './MessageBubble.scss';

// Smooths a streaming bubble. Buffered text can grow in uneven bursts (one
// delivery can carry a sentence, the next a word), faster than a comfortable
// reveal — so useSmoothedText eases the reveal toward the buffered text, covering a
// frame-rate-independent fraction of the remaining distance each frame. It follows the buffer
// fluidly and decelerates as it catches up, instead of snapping through catch-up jumps. Once a
// bubble has streamed it keeps easing to the very end even after the server marks it done, so the
// final words glide in rather than popping. A row that never streamed (history, a reload mid-thread)
// — or a reader who prefers reduced motion — renders in full at once.
const STREAM_FOLLOW_RATE = 11; // exponential follow toward the buffered target
const STREAM_MIN_CPS     = 80; // chars/sec floor so the reveal never crawls as it nears the buffer

const prefersReducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const useSmoothedText = (fullText, isStreaming) => {
  const text      = fullText || '';
  const targetRef = useRef(text);
  targetRef.current = text;
  // Float cursor in a ref so the loop reads it without re-subscribing; mirrored to state to render.
  const revealRef = useRef(isStreaming ? 0 : text.length);
  const streamedRef = useRef(isStreaming);
  if (isStreaming) streamedRef.current = true;
  const [revealed, setRevealed] = useState(revealRef.current);

  useEffect(() => {
    if (prefersReducedMotion() || !streamedRef.current) {
      revealRef.current = targetRef.current.length;
      setRevealed(revealRef.current);
      return undefined;
    }
    let raf;
    let last = null;
    const tick = now => {
      const target = targetRef.current.length;
      // Cap dt so a backgrounded tab doesn't reveal a whole paragraph in one frame on return.
      const dtSec  = last == null ? 0 : Math.min((now - last) / 1000, 0.05);
      last = now;
      if (revealRef.current < target) {
        const remaining = target - revealRef.current;
        const eased     = remaining * (1 - Math.exp(-dtSec * STREAM_FOLLOW_RATE));
        revealRef.current = Math.min(target, revealRef.current + Math.max(STREAM_MIN_CPS * dtSec, eased));
        setRevealed(revealRef.current);
      }
      // Keep ticking while the server streams more, and afterward until the reveal catches the end.
      if (isStreaming || revealRef.current < targetRef.current.length) {
        raf = requestAnimationFrame(tick);
      }
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [isStreaming]);

  return streamedRef.current
    ? text.slice(0, Math.min(Math.floor(revealed), text.length))
    : text;
};

// GFM extends the markdown parser with tables, strikethrough, task
// lists and autolinks. Without it, ReactMarkdown ignores the agent's
// table syntax entirely and emits a runs-on paragraph.
const REMARK_PLUGINS = [gfm];

// Default link renderer: an internal href (a path like "/list/9652") gets a plain anchor —
// routing-aware consumers override via `renderLink` (e.g. with a react-router Link);
// external links open in a new tab.
const defaultRenderLink = safeMarkdownLink;

// Turn a bare internal path the agent typed (e.g. "…at /list/9652.") into a markdown link so it
// renders clickable. Code spans/fences are split out first and left untouched, and a path is only
// matched at start-of-segment or after whitespace, so existing [label](/path) links are skipped.
const linkifyInternalPaths = md => {
  if (!md) return md;
  return md
    .split(/(```[\s\S]*?```|`[^`]*`)/g)
    .map(seg =>
      seg.startsWith('`')
        ? seg
        : seg.replace(/(^|\s)(\/[a-zA-Z][\w-]*(?:\/[\w-]+)*)/g, (match, pre, path) => `${pre}[${path}](${path})`)
    )
    .join('');
};

// Bubbles stream, so the boundary recovers when the text grows past a transiently-broken state.
const renderBubbleFallback = text => <pre className='ap-message-bubble-fallback'>{text}</pre>;

// Inverted app couplings: `onRetry(message)` performs the resend, `renderLink` swaps the
// markdown link element, and `renderMeta(message)` / `renderActions(message)` are slots for
// the caller's meta line and actions row — whatever state or routing those need stays with
// the consumer.
const MessageBubble = ({
  message,
  AgentThread_id,
  onRetry,
  renderLink,
  renderMeta,
  renderActions,
  lifecycleEventsVisible,
  onReplayLifecycleEvent
}) => {
  const [isRetrying, setIsRetrying] = useState(false);
  const isUser = message.role === 'user';
  const isStreaming = !!message._isStreaming;
  const isOptimistic = !!message._optimistic;
  // A persisted message whose agent hand-off failed (metadata.deliveryFailed) reuses
  // the same failed/Retry surface as a send that never reached the server.
  const isFailed = !!message._failed || !!(message.metadata && message.metadata.deliveryFailed);
  const streamedBody = useSmoothedText(message.message || '', isStreaming && !isUser);
  // True until the eased reveal reaches the buffered text — outlives the server's done flag by the
  // brief catch-up tail, so the caret and edge fade stay until the last words have actually landed.
  const isRevealing = !isUser && streamedBody.length < (message.message || '').length;

  // Wrap rendered tables in a scroll container so wide agent tables
  // (8+ columns is common when the agent dumps a full slate) don't blow
  // out the bubble width — they scroll horizontally within the wrapper.
  const linkRenderer = renderLink || defaultRenderLink;
  const markdownRenderers = useMemo(() => ({
    table: ({ children, ...rest }) => (
      <div className='ap-message-bubble-table-wrap'>
        <table {...rest}>{children}</table>
      </div>
    ),
    link: ({ href, children }) => linkRenderer({ href, children }),
    // Reference-style links ([text][ref] + a definition) resolve through their
    // own renderer — without this they'd bypass the allowlist entirely, and
    // the default uriTransformer waves protocol-relative hrefs (//evil.com)
    // through.
    linkReference: ({ href, children }) => linkRenderer({ href, children })
  }), [linkRenderer]);

  const handleRetry = async () => {
    const clientMessageId = message.metadata && message.metadata.clientMessageId;
    if (!AgentThread_id || !clientMessageId || !onRetry) return;
    setIsRetrying(true);
    try { await onRetry(message); }
    finally { setIsRetrying(false); }
  };

  const stamp = message.createdAt ? moment(message.createdAt) : null;
  const labelTime = stamp ? stamp.format('h:mma') : '';

  return (
    <>
      <div
        className={cn('ap-message-bubble', {
          'is-user':       isUser,
          'is-autopilot':  !isUser,
          'is-streaming':  isStreaming || isRevealing,
          'is-optimistic': isOptimistic,
          'is-failed':     isFailed
        })}
        role='article'
        aria-roledescription='message'
        aria-label={`${message.role} message${labelTime ? ` at ${labelTime}` : ''}`}
      >
        {isUser
          ? <span>{message.message}</span>
          : (
            <div className='ap-message-bubble-markdown'>
              <MarkdownSafeBoundary text={message.message || ''} recoversOnTextChange renderFallback={renderBubbleFallback}>
                <ReactMarkdown plugins={REMARK_PLUGINS} renderers={markdownRenderers}>
                  {linkifyInternalPaths(streamedBody)}
                </ReactMarkdown>
              </MarkdownSafeBoundary>
            </div>
          )
        }
        {!isUser && (
          <LifecycleEventChips
            events={(message.metadata?.lifecycleEvents || []).map(e => ({ ...e, row: message }))}
            visible={lifecycleEventsVisible}
            onReplay={onReplayLifecycleEvent}
          />
        )}
        {(isStreaming || isRevealing) && <span className='ap-message-bubble-caret' aria-hidden='true' />}
        {isFailed && (
          <button
            type='button'
            className='ap-message-bubble-retry'
            onClick={handleRetry}
            disabled={isRetrying}
            aria-label='Retry sending message'
          >
            {isRetrying ? 'Retrying\u2026' : 'Retry'}
          </button>
        )}
      </div>
      {!isUser && !isStreaming && !isFailed && renderActions ? renderActions(message) : null}
      {renderMeta ? renderMeta(message) : null}
    </>
  );
};

MessageBubble.propTypes = {
  message:            PropTypes.shape({
    // DB row ids (numbers) or derived string ids (`m<row.id>` / tool-call ids).
    id:        PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    role:      PropTypes.string.isRequired,
    message:   PropTypes.string,
    metadata:  PropTypes.object,
    createdAt: PropTypes.string,
    _isStreaming: PropTypes.bool,
    _optimistic:  PropTypes.bool,
    _failed:      PropTypes.bool
  }).isRequired,
  // Numeric DB thread ids or string thread refs (Moment:<id>).
  AgentThread_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onRetry:        PropTypes.func,
  renderLink:     PropTypes.func,
  renderMeta:     PropTypes.func,
  renderActions:  PropTypes.func,
  lifecycleEventsVisible: PropTypes.bool,
  onReplayLifecycleEvent: PropTypes.func
};

export default MessageBubble;
